import React from 'react';
import { Box, Button, CircularProgress, Stack, Tooltip } from '@mui/material';
import { createGlobalStyle } from 'styled-components';
import { theme } from '../../../../../../common/styles/theme';
import { Text } from '../../../../../../common/components/Text/Text';
import { Modal } from '../../../../../../common/components/Modal/Modal';
import { EndlessBookIcon } from '../../../../../../../icons/EndlessBookIcon';
import { SpeakerIcon } from '../../../../../../../icons/SpeakerIcon';
import { TranslationsIcon } from '../../../../../../../icons/TranslationsIcon';
import { SettingsIcon } from '../../../../../../../icons/SettingsIcon';
import { ArrowIcon } from '../../../../../../../icons/ArrowIcon';
import { CloseIcon } from '../../../../../../../icons/CloseIcon';
import { BOOK_EMBED_CSS, BookReader } from '../../../../../../common/endlessBook/embed';
import { BookSettingsFormHOC } from '../BookSettingsForm/hocs/BookSettingsFormHOC';
import { BookSettingsAccordion } from './BookSettingsAccordion';
import { BookProgress, CREATE_MESSAGES, REGENERATE_MESSAGES } from './BookProgress';
import { IBookTabProps } from './types';

// every selector in it is scoped under .eb-embed (the reader's root); then meet's colors on top
const BookReaderStyle = createGlobalStyle`
	${BOOK_EMBED_CSS}
	.eb-embed {
		--ink: ${theme.palette.grey[900]};
		--ink-soft: ${theme.palette.grey[500]};
		--accent: ${theme.palette.primary.main};
		--accent-dark: ${theme.palette.primary[500]};
		background: transparent;
		font-family: ${theme.typography.fontFamily};
	}
	/* level badge: plain grey text (!important beats the badge's inline per-level colour) */
	.eb-embed .cefr-badge {
		background: none !important;
		color: ${theme.palette.grey[500]};
	}
	/* header / nav round buttons: grey, no border; a toggle that's on is primary */
	.eb-embed .reader-icon-btn {
		border: none;
		/* a step darker than the page around the reader (grey[100]) */
		background: ${theme.palette.grey[200]};
		color: ${theme.palette.grey[500]};
	}
	.eb-embed .reader-icon-btn:hover:not(:disabled):not(.active) {
		background: ${theme.palette.grey[300]};
	}
	.eb-embed .reader-icon-btn.is-on,
	.eb-embed .reader-icon-btn.is-on:hover:not(:disabled):not(.active) {
		background: ${theme.palette.primary.main};
		color: ${theme.palette.common.white};
		box-shadow: none;
	}
	/* "Opening your book…" / load error */
	.eb-embed .centered-msg {
		font-size: ${theme.typography.subtitle2.fontSize}px;
		color: ${theme.palette.grey[500]};
	}
	/* per-line read-aloud button: our speaker icon reads small at the reader's .78rem */
	.eb-embed .line-speak {
		font-size: 1.1rem;
	}
	/* the book page keeps endless-book's own ink */
	.eb-embed .page-sheet {
		--ink: #2c2620;
		--ink-soft: #6b6256;
	}
	/* loading word-game cards: state is shown by the fill alone */
	.eb-embed .match-item {
		border: none;
	}
	.eb-embed .match-game-done {
		font-size: 1.1rem;
	}
	.eb-embed .reader-foot {
		margin-bottom: ${theme.spacing(3)};
	}
` as any;

// meet's icons in place of the reader's emoji
const ICON_SX = {
	fontSize: 20,
};
const READER_ICONS = {
	audio: <SpeakerIcon sx={ICON_SX} />,
	translate: <TranslationsIcon sx={ICON_SX} />,
	settings: <SettingsIcon sx={ICON_SX} />,
	prev: <ArrowIcon sx={{ ...ICON_SX, transform: 'rotate(180deg)' }} />,
	next: <ArrowIcon sx={ICON_SX} />,
	// word popup / line read-aloud: sized by the reader's own font-size there
	speak: <SpeakerIcon sx={{ fontSize: '1em' }} />,
	close: <CloseIcon sx={{ fontSize: '.8em' }} />,
};

// The reader's header / nav hints as our tooltip; the span keeps it on disabled buttons too.
const readerTooltip = (title: React.ReactNode, element: React.ReactElement) => (
	<Tooltip
		// our tooltip is styled for one line; markup (the level hint) gets a padded, narrower block
		title={typeof title === 'string' ? title : <Box sx={{ py: 1, maxWidth: 280, lineHeight: 1.4 }}>{title}</Box>}
		placement={'bottom'}
		enterDelay={300}
		disableInteractive
	>
		<span style={{ display: 'inline-flex' }}>{element}</span>
	</Tooltip>
);

export const BookTab: React.FC<IBookTabProps> = ({
	bookId,
	canCreateBook,
	creating,
	regenerating,
	bookHost,
	bookHostFailed,
	reloadKey,
	onCreateBook,
	onLoadBookHost,
	onGetToken,
	onBook,
	onReaderError,
}) => {

	React.useEffect(() => {
		if (bookId) onLoadBookHost();
	}, [bookId]);

	// settings opened from the reader's ⚙ (pages after the first); regenerating takes the tab over
	const [settingsOpen, setSettingsOpen] = React.useState(false);
	React.useEffect(() => {
		if (regenerating) setSettingsOpen(false);
	}, [regenerating]);

	if (creating) {
		return <BookProgress title={'Creating your book'} messages={CREATE_MESSAGES} />;
	}

	if (bookId) {
		if (!bookHost) {
			return (
				<Stack alignItems={'center'} justifyContent={'center'} gap={2} sx={{ flex: '1 1 auto', minHeight: 0 }}>
					{bookHostFailed
						? <>
							<Text sx={{ fontSize: theme.typography.subtitle1, color: theme.palette.grey[500] }}>
								Couldn’t open the book right now
							</Text>
							<Button variant={'contained'} onClick={onLoadBookHost}>Try again</Button>
						</>
						: <CircularProgress />
					}
				</Stack>
			);
		}
		return (
			<Box
				sx={{
					position: 'relative',
					flex: '1 1 auto',
					minHeight: 0,
					overflow: 'hidden',
				}}
			>
				<BookReaderStyle />
				{/* the reader's root is its own scroll container and needs a definite height */}
				<Box sx={{ position: 'absolute', inset: 0 }}>
					<BookReader
						key={`${bookId}-${reloadKey}`}
						bookId={bookId}
						apiBase={bookHost}
						getToken={onGetToken}
						topSlot={<BookSettingsAccordion><BookSettingsFormHOC bookId={bookId} /></BookSettingsAccordion>}
						onOpenSettings={() => setSettingsOpen(true)}
						icons={READER_ICONS}
						tooltip={readerTooltip}
						onBook={onBook}
						onError={onReaderError}
					/>
				</Box>
				{regenerating &&
					<Stack
						sx={{
							position: 'absolute',
							inset: 0,
							zIndex: 1,
							background: 'rgba(255, 255, 255, .9)',
						}}
					>
						<BookProgress title={'Regenerating your book'} messages={REGENERATE_MESSAGES} />
					</Stack>
				}
				<Modal
					title={'Book settings'}
					open={settingsOpen}
					onClose={() => setSettingsOpen(false)}
					PaperProps={{ sx: { width: theme.spacing(80) } }}
				>
					<BookSettingsFormHOC bookId={bookId} />
				</Modal>
			</Box>
		);
	}

	return (
		<Stack
			alignItems={'center'}
			justifyContent={'center'}
			gap={2}
			sx={{
				flex: '1 1 auto',
				minHeight: 0,
			}}
		>
			<EndlessBookIcon sx={{ width: theme.spacing(6), height: theme.spacing(6), color: theme.palette.grey[400] }} />

			{canCreateBook
				? <>
					<Text sx={{ fontSize: theme.typography.subtitle1, fontWeight: 600 }}>Turn this lesson into a book</Text>
					<Button variant={'contained'} onClick={onCreateBook}>Create new book</Button>
				</>
				: <Text sx={{ fontSize: theme.typography.subtitle1, color: theme.palette.grey[400] }}>
					A book can be created once the recording has a transcript
				</Text>
			}
		</Stack>
	);
};
